import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, MenuItem } from '@mui/material';
import 'dayjs/locale/ko';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; 
import DateAdapter from '@mui/lab/AdapterDateFns';


import { downloadFile } from '../../utils/fileCodeUtils';
import { useSnackbar } from 'notistack';
import * as salesCosts from '../../lib/api/salesCosts';
import MobileSearchAdornment from '../common/search/MobileSearchAdornment';
import MobileAddFab from '../common/search/MobileAddFab';

//----------------------------------------------------------------------------

export const SalesCostListBox = ({
  handleAdd,
  formik,
  onUpload,
  companyOptions,
}) => {
  const [searchStrtDt, setSearchStrtDt] = useState(null);
  const [searchEndDt, setSearchEndDt] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (formik.values.searchStrtDt) {
      setSearchStrtDt(dayjs(formik.values.searchStrtDt));
    }
    if (formik.values.searchEndDt) {
      setSearchEndDt(dayjs(formik.values.searchEndDt));
    }
  }, [formik.values.searchStrtDt, formik.values.searchEndDt]);

  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };


const handleDownload = async () => {
  try {

    const selectedYear = searchStrtDt 
      ? new Date(searchStrtDt).getFullYear() 
      : new Date().getFullYear();

    const blob = await salesCosts.downloadExl({
      year: selectedYear,
    });
    const filename = blob.headers['filename'];

    downloadFile(blob, decodeURIComponent(filename), 'xlsx');
  } catch (err) {
    let message =
      typeof err.response !== 'undefined'
        ? err.response.data.message
        : err.message;
    enqueueSnackbar(message, {
      variant: 'error',
    });
  }
};

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignItems: 'center',
          my: 1,
        }}
      >
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            flexGrow: 1,
            display: { xs: 'contents', sm: 'flex' },
            flexDirection: 'row',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            alignItems: 'center',
            rowGap: 1,
            mr: 2,
          }}
          onSubmit={formik.handleSubmit}
        >
          <Box
            sx={{
              order: { xs: 1, sm: 0 },
              flexBasis: { xs: '100%', sm: 'auto' },
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
              alignItems: 'center',
              '& > :not(style)': { m: { xs: 0.5, sm: 1 }, width: { sm: '20ch' } },
              // xs: 회사(30%) + 검색키워드(30%) + 검색어(나머지, 돋보기)
              '& > :nth-of-type(1)': { flex: { xs: '0 0 28%', sm: '0 1 auto' } },
              '& > :nth-of-type(2)': { flex: { xs: '0 0 28%', sm: '0 1 auto' } },
              '& > :nth-of-type(3)': { flex: { xs: 1, sm: '0 1 auto' } },
            }}
          >
            <TextField
              variant="standard"
              required={false}
              select
              id="companyCd"
              name="companyCd"
              label="회사"
              value={formik.values?.companyCd}
              onChange={formik.handleChange}
              error={
                formik.touched.companyCd && Boolean(formik.errors.companyCd)
              }
              helperText={formik.touched.companyCd && formik.errors.companyCd}
            >
              <MenuItem value="ALL">전체</MenuItem>
              {companyOptions.map((option) => (
                <MenuItem key={option.companyCd} value={option.companyCd}>
                  {option.companyNm}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              variant="standard"
              required={false}
              select
              id="searchKeyword"
              name="searchKeyword"
              label="검색키워드"
              value={formik.values.searchKeyword}
              onChange={formik.handleChange}
              error={
                formik.touched.searchKeyword &&
                Boolean(formik.errors.searchKeyword)
              }
              helperText={
                formik.touched.searchKeyword && formik.errors.searchKeyword
              }
            >
              <MenuItem value="scLarge">계정과목(대)</MenuItem>
              <MenuItem value="scSmall">계정과목(소)</MenuItem>
              <MenuItem value="scWh">사용처</MenuItem>
              <MenuItem value="companyNm">회사명</MenuItem>
              <MenuItem value="crtNm">등록자</MenuItem>
            </TextField>
            <TextField
              variant="standard"
              required={false}
              id="searchWords"
              name="searchWords"
              label="검색어"
              value={formik.values.searchWords}
              onChange={formik.handleChange}
              error={
                formik.touched.searchWords && Boolean(formik.errors.searchWords)
              }
              helperText={
                formik.touched.searchWords && formik.errors.searchWords
              }
              InputProps={{
                endAdornment: <MobileSearchAdornment disabled={formik.isSubmitting} />,
              }}
            />
          </Box>
          <Box
            sx={{
              order: { xs: 2, sm: 0 },
              flexBasis: { xs: '100%', sm: 'auto' },
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 0 },
            }}
          >
            <Box sx={{ flex: { xs: 1, sm: '0 1 auto' }, minWidth: 0, width: { sm: 'auto' } }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                <DatePicker
                  label="검색 시작일"
                  format="YYYY-MM-DD"
                  mask={'____-__-__'}
                  id="searchStrtDt"
                  name="searchStrtDt"
                  value={dayjs(searchStrtDt)}
                  onChange={(newValue) => {
                    setSearchStrtDt(dayjs(newValue));
                    onChangeDate(dayjs(newValue), 'searchStrtDt');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      error={
                        formik.touched.searchStrtDt &&
                        Boolean(formik.errors.searchStrtDt)
                      }
                      helperText={
                        formik.touched.searchStrtDt && formik.errors.searchStrtDt
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: { xs: 1, sm: '0 1 auto' }, minWidth: 0, width: { sm: 'auto' }, mx: { xs: 0, sm: 2 } }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                <DatePicker
                  label="검색 종료일"
                  format="YYYY-MM-DD"
                  mask={'____-__-__'}
                  id="searchEndDt"
                  name="searchEndDt"
                  value={dayjs(searchEndDt)}
                  onChange={(newValue) => {
                    setSearchEndDt(dayjs(newValue));
                    onChangeDate(dayjs(newValue), 'searchEndDt');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      error={
                        formik.touched.searchEndDt &&
                        Boolean(formik.errors.searchEndDt)
                      }
                      helperText={
                        formik.touched.searchEndDt && formik.errors.searchEndDt
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Button
              type="submit"
              color="info"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              검색
            </Button>
          </Box>
        </Box>
        <Box textAlign="center" sx={{ mx: 1, display: { xs: 'none', sm: 'block' } }}>
          <Button color="primary" variant="contained" onClick={handleAdd}>
            추가
          </Button>
        </Box>
        <Box
          sx={{
            order: { xs: 3, sm: 0 },
            flexBasis: { xs: '100%', sm: 'auto' },
            display: 'flex',
            flexDirection: 'row',
            justifyContent: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            mt: { xs: 0.5, sm: 0 },
          }}
        >
          <Box textAlign="center" sx={{ mx: { xs: 0, sm: 1 } }}>
            <Button
              color="success"
              variant="contained"
              onClick={onUpload}
              sx={{
                fontSize: { xs: 12, sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 0.75 },
                minWidth: { xs: 0, sm: 64 },
              }}
            >
              업로드
            </Button>
          </Box>
          <Box textAlign="center">
            <Button
              color="success"
              variant="contained"
              onClick={handleDownload}
              sx={{
                fontSize: { xs: 12, sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 0.75 },
                minWidth: { xs: 0, sm: 64 },
              }}
            >
              다운
            </Button>
          </Box>
        </Box>
        <MobileAddFab onClick={handleAdd} />
      </Box>
    </div>
  );
};
